import { useState, useEffect } from 'react';
import { Search, FileText, Users, Wrench, ArrowRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const TOUR_STEPS = [
  { icon: Search, title: "Find WFH Jobs", desc: "Browse thousands of remote jobs filtered by category, state, equipment, and more. Use Quick Apply to submit in one click.", highlight: "/jobs" },
  { icon: FileText, title: "AI Resume Builder", desc: "Build a professional resume with AI assistance from GPT, Claude, or Gemini. Tailored for remote work positions.", highlight: "/resume-builder" },
  { icon: Wrench, title: "WFH Tools", desc: "Test your typing speed, internet connection, and complete your WFH readiness checklist before applying.", highlight: "/tools" },
  { icon: Users, title: "Community & Events", desc: "Join community groups, attend live hiring events, and network with other remote workers.", highlight: "/community" },
];

export default function OnboardingTour({ onComplete }) {
  const [step, setStep] = useState(0);

  const handleNext = () => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      onComplete();
    }
  };

  const current = TOUR_STEPS[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="onboarding-tour">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onComplete} />
      <div className="relative bg-white dark:bg-[hsl(200,10%,14%)] rounded-3xl border border-[#E5E0D8] dark:border-[hsl(200,10%,20%)] shadow-[0_20px_60px_rgb(0,0,0,0.2)] max-w-md w-full mx-6 p-8 animate-fade-up">
        {/* Close */}
        <button onClick={onComplete} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F4F1E8] dark:bg-[hsl(200,10%,20%)] flex items-center justify-center hover:bg-[#E5E0D8] transition-colors" data-testid="tour-close">
          <X size={14} className="text-[#1C2A24]/50 dark:text-[hsl(40,20%,60%)]" strokeWidth={1.5} />
        </button>

        {/* Progress dots */}
        <div className="flex gap-1.5 mb-6">
          {TOUR_STEPS.map((_, i) => (
            <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i === step ? 'w-8 bg-[#047857]' : i < step ? 'w-4 bg-[#047857]/30' : 'w-4 bg-[#E5E0D8]'}`} />
          ))}
        </div>

        {/* Content */}
        <div className="w-14 h-14 rounded-2xl bg-[#047857]/10 flex items-center justify-center mb-5">
          <Icon size={24} className="text-[#047857]" strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-medium mb-2" style={{fontFamily:'Outfit'}}>{current.title}</h2>
        <p className="text-sm text-[#1C2A24]/60 dark:text-[hsl(40,20%,60%)] leading-relaxed mb-6">{current.desc}</p>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button onClick={onComplete} className="text-sm text-[#1C2A24]/40 hover:text-[#1C2A24]/60 transition-colors" data-testid="tour-skip">Skip tour</button>
          <Button onClick={handleNext} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-10 px-6 text-sm" data-testid="tour-next">
            {step < TOUR_STEPS.length - 1 ? (
              <>Next <ArrowRight size={14} className="ml-1" strokeWidth={1.5} /></>
            ) : (
              "Get Started"
            )}
          </Button>
        </div>

        <p className="text-xs text-[#1C2A24]/30 dark:text-[hsl(40,20%,40%)] mt-4 text-center">{step + 1} of {TOUR_STEPS.length}</p>
      </div>
    </div>
  );
}
